import { TGraphicDesignLessonConfig } from '../../../models/courses/graphicDesign';
import { RULE_TYPES_BY_EXERCISE } from './ruleTemplates';
import { deriveCritiqueConfig } from '../student/critique';

export interface TConfigIssue { level: 'error' | 'warning'; field: string; message: string }
export const validateGraphicDesignConfig = (config: TGraphicDesignLessonConfig): TConfigIssue[] => {
  const issues: TConfigIssue[] = [];
  const add = (level: TConfigIssue['level'], field: string, message: string) =>
    issues.push({ level, field, message });
  const ids = new Set<string>();
  config.rules.forEach((rule, index) => {
    const field = `rules.${index}`;
    const name = `L'objectif « ${rule.label.fr || rule.id} »`;
    if (ids.has(rule.id)) add('error', field,
      `Identifiant d'objectif en double : « ${rule.label.fr || rule.id} ».`);
    ids.add(rule.id);
    if (!RULE_TYPES_BY_EXERCISE[config.exerciseType].includes(rule.type)) {
      add('error', field, `${name} ne convient pas à cet exercice.`);
    }
    const params = rule.params as unknown as Record<string, unknown>;
    const selectors = [params.select, params.a, params.b, params.order]
      .flatMap(value => Array.isArray(value) ? value : [value]);
    if (selectors.some(value => value && typeof value === 'object' &&
      'by' in value && ['text', 'role', 'icon', 'id'].includes(String(value.by)) &&
      !String(value.value ?? '').trim())) {
      add('warning', field, `Objectif « ${rule.label.fr || rule.id} » : précisez l'élément visé.`);
    }
    if (rule.type === 'choiceEquals' && !((config.exerciseType === 'INTENT' &&
      (config.versions?.length ?? 0) <= 1 ? config.choices : config.versions) ?? [])
      .some(item => item.id === rule.params.choiceId)) {
      add('error', field, `Le choix de ${name} est inconnu.`);
    }
    if (rule.type === 'reasonEquals' && !(config.choices ?? [])
      .some(item => item.id === rule.params.reasonId)) {
      add('error', field, `La raison de ${name} est inconnue.`);
    }
  });
  // La critique genere ses regles depuis les zones : le seuil se compare a celles-ci.
  const effective = config.exerciseType === 'CRITIQUE' ? deriveCritiqueConfig(config) : config;
  if (config.requiredRules > effective.rules.filter(rule => rule.tier === 'required').length) {
    add('error', 'requiredRules', 'Le seuil dépasse le nombre de règles obligatoires.');
  }
  if (config.exerciseType === 'CRITIQUE') {
    if (!config.zones?.length) add('error', 'zones', 'Ajoutez au moins une zone.');
    config.zones?.forEach((zone, index) => {
      if (zone.options.length < 2 || zone.correct < 0 || zone.correct >= zone.options.length) {
        add('error', `zones.${index}`, 'Chaque zone demande deux options et une réponse valide.');
      }
    });
  }
  if (config.exerciseType === 'CHOOSE') {
    if ((config.versions?.length ?? 0) < 2) add('error', 'versions', 'Ajoutez deux versions.');
    if (!config.rules.some(rule => rule.type === 'choiceEquals')) {
      add('error', 'rules', 'Choisissez la version attendue.');
    }
  }
  if (config.initialState?.mode === 'fromLesson' && config.initialState.lessonId <= 0) {
    add('error', 'initialState.lessonId', 'Choisissez une leçon de départ valide.');
  }
  if (!config.hints.length) add('warning', 'hints', 'Ajoutez au moins un indice.');
  if (config.hints.length > 3) add('warning', 'hints', 'Limitez les indices à trois.');
  if (!config.brief.fr.trim()) add('warning', 'brief.fr', 'Renseignez le brief en français.');
  if (['CREATE', 'TRANSFORM'].includes(config.exerciseType) ||
    (config.exerciseType === 'INTENT' && !config.versions?.length)) {
    if (!config.solution) add('warning', 'solution', 'Ajoutez une solution de référence.');
  }
  if (config.backgroundImage && !/^https?:\/\//i.test(config.backgroundImage)) {
    add('warning', 'backgroundImage', 'L’image doit utiliser une adresse http(s).');
  }
  return issues;
};
